import axiosClient from "utils/axios";
import { ApiOptionList, ApiResponse } from "types/api.types";
import type { StockLocationType } from "../constants/stock-location.constants";
import {
  CreateStockLocationRequest,
  StockLocation,
  StockLocationsTableResponse,
  StockLocationStockResponse,
  UpdateStockLocationRequest,
} from "../types/stock-location.types";

function normalizeListParams(
  page: number,
  limit: number,
  rows: StockLocation[],
): StockLocationsTableResponse {
  const totalItems = rows.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / limit));
  const start = (page - 1) * limit;
  return {
    rows: rows.slice(start, start + limit),
    totalItems,
    currentPage: page,
    pageSize: limit,
    limit,
    totalPages,
  };
}

export const getStockLocations = async ({
  page = 1,
  limit = 10,
  search,
  type,
}: {
  page?: number;
  limit?: number;
  search?: string;
  type?: StockLocationType;
} = {}) => {
  const { data } = await axiosClient.get<
    ApiResponse<StockLocationsTableResponse | { rows: StockLocation[] }>
  >("/stock-locations", {
    params: {
      query: search || undefined,
      type,
    },
  });

  const payload = data.data;
  const allRows =
    payload && typeof payload === "object" && Array.isArray(payload.rows)
      ? payload.rows
      : [];

  return {
    success: data.success,
    message: data.message,
    data: normalizeListParams(page, limit, allRows),
  } satisfies ApiResponse<StockLocationsTableResponse>;
};

export const getStockLocationOptions = async ({
  query,
  page = 1,
  limit = 50,
  type,
}: {
  query?: string;
  limit?: number;
  page?: number;
  type?: StockLocationType;
}) => {
  const response = await axiosClient.get<ApiResponse<ApiOptionList>>(
    "/stock-locations/options",
    {
      params: {
        query: query?.trim() || undefined,
        limit,
        page,
        type,
      },
    },
  );
  return response.data;
};

export const getStockLocation = async (id: number) => {
  const response = await axiosClient.get<ApiResponse<StockLocation>>(
    `/stock-locations/${id}`,
  );
  return response.data;
};

export const getStockLocationStock = async (id: number) => {
  const response = await axiosClient.get<
    ApiResponse<StockLocationStockResponse>
  >(`/stock-locations/${id}/stock`);
  return response.data;
};

export const createStockLocation = async (
  payload: CreateStockLocationRequest,
) => {
  const response = await axiosClient.post<ApiResponse<StockLocation>>(
    "/stock-locations",
    payload,
  );
  return response.data;
};

export const updateStockLocation = async (
  id: number,
  payload: UpdateStockLocationRequest,
) => {
  const response = await axiosClient.put<ApiResponse<StockLocation>>(
    `/stock-locations/${id}`,
    payload,
  );
  return response.data;
};

export const deleteStockLocation = async (id: number) => {
  const response = await axiosClient.delete<ApiResponse<unknown>>(
    `/stock-locations/${id}`,
  );
  return response.data;
};
